import { useState, useRef, useCallback } from 'react';
import { Button, Select, SelectItem } from '@heroui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { KnowledgeItem, DrawingPage } from '@estimator/shared';

interface PageSelectorProps {
  drawings: KnowledgeItem[];
  currentDrawingId: string | null;
  pages: DrawingPage[];
  currentPageIndex: number;
  onDrawingChange: (drawingId: string) => void;
  onPageChange: (index: number) => void;
}

export default function PageSelector({
  drawings,
  currentDrawingId,
  pages,
  currentPageIndex,
  onDrawingChange,
  onPageChange,
}: PageSelectorProps) {
  const currentDrawing = drawings.find((d) => d.id === currentDrawingId);
  const totalPages = pages.length;
  const currentPage = pages[currentPageIndex];

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  const handleFocus = useCallback(() => {
    setIsEditing(true);
    setInputValue(String(currentPageIndex + 1));
    // select all on next tick so the value is set
    requestAnimationFrame(() => inputRef.current?.select());
  }, [currentPageIndex]);

  const commitPage = useCallback(() => {
    setIsEditing(false);
    const num = parseInt(inputValue, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num - 1);
    }
  }, [inputValue, totalPages, onPageChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        commitPage();
        inputRef.current?.blur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        inputRef.current?.blur();
      }
    },
    [commitPage],
  );

  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 border-b"
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border)',
      }}
    >
      {/* Drawing selector */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Select
          size="sm"
          aria-label="Select drawing"
          selectedKeys={currentDrawingId ? [currentDrawingId] : []}
          onSelectionChange={(keys) => {
            const key = Array.from(keys)[0] as string;
            if (key) onDrawingChange(key);
          }}
          classNames={{
            base: 'max-w-[240px]',
            trigger:
              'h-8 min-h-8 border-1 border-[var(--border)] bg-[var(--bg-panel)] data-[hover=true]:border-[var(--border-em)]',
            value: 'text-xs text-[var(--text-primary)]',
            popoverContent: 'bg-[var(--bg-panel)] border border-[var(--border)]',
          }}
        >
          {drawings.map((d) => (
            <SelectItem key={d.id} textValue={d.title}>
              <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
                {d.title}
              </span>
            </SelectItem>
          ))}
        </Select>

        {/* Item title info */}
        {currentDrawing && (
          <span className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>
            {currentDrawing.title}
          </span>
        )}
      </div>

      {/* Page navigation */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          isIconOnly
          size="sm"
          variant="light"
          isDisabled={currentPageIndex <= 0}
          onPress={() => onPageChange(currentPageIndex - 1)}
          aria-label="Previous page"
          className="min-w-7 w-7 h-7 text-[var(--text-secondary)]"
        >
          <ChevronLeft size={14} />
        </Button>

        <span
          className="text-xs font-ui text-center tabular-nums flex items-center gap-1"
          style={{ color: 'var(--text-secondary)' }}
        >
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            value={isEditing ? inputValue : String(currentPageIndex + 1)}
            onFocus={handleFocus}
            onBlur={commitPage}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Page number"
            className="w-7 h-6 text-center text-xs font-ui tabular-nums rounded border outline-none transition-colors"
            style={{
              backgroundColor: isEditing ? 'var(--bg-base)' : 'transparent',
              borderColor: isEditing ? 'var(--accent)' : 'var(--border)',
              color: 'var(--text-primary)',
            }}
          />
          <span className="select-none">/ {totalPages}</span>
        </span>

        <Button
          isIconOnly
          size="sm"
          variant="light"
          isDisabled={currentPageIndex >= totalPages - 1}
          onPress={() => onPageChange(currentPageIndex + 1)}
          aria-label="Next page"
          className="min-w-7 w-7 h-7 text-[var(--text-secondary)]"
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
}
